/**
 * Umpire appointments engine — the advisory draft (U1 / U2 / RU / VU), module 02.
 *
 * Authority:
 *   - `docs/specs/control-centre/03-appointments.md` **Part B** — §B1 (the TD supervises, the
 *     Umpires Managers appoint), §B5 (the five umpire rules and *"the deliberate asymmetry"*),
 *     §B6 (*"no combined total"*), §B8 (three figures, never one), §B9 (the AI reference
 *     section and the evenness note) and §B10 (the dropdown's three blocked-option strings);
 *   - Part C invariants 1 (every matrix derived live, never stored), 5 (the read-only dataset
 *     and the advisory dataset must never cross) and 6 (bind to `tms_match_db_id`);
 *   - `docs/specs/control-centre/07-configuration.md` §06 — the threshold
 *     `umpire_onfield_per_day` and the rule `Umpire Daily Load`;
 *   - the Technical Delegate's instruction of 2026-08-28 introducing **Avoid VU** (see §4
 *     below and migration 039);
 *   - the Technical Delegate's instruction of 2026-09-01 introducing **Cross Gender**, the
 *     **Umpire Type** pairing rule and the consecutive-days limit (see §4A below and
 *     migration 042).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS ENGINE IS FOR, AND WHAT IT MUST NEVER BE READ AS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Nothing here is an appointment. Umpire appointments are the Umpires Managers' (§B1) and
 * reach this system as the read-only `tms_actual` dataset. What this engine produces is the
 * **yardstick**: an evenly-spread draft the TD can hold the Managers' actual distribution up
 * against, so he can see whether opportunities have been spread fairly. §B9 is explicit —
 * *"Not a target and never pushed anywhere"*.
 *
 * It therefore has two entry points and they do different jobs:
 *
 *   * {@link draftUmpireAppointments} chooses — it fills the advisory `ai_reference` set.
 *   * {@link evaluateUmpireAppointments} measures — it recomputes violations and workloads
 *     from rows already stored, and is what a GET runs over BOTH datasets. It chooses nothing,
 *     which is why it is safe to run over `tms_actual`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DELIBERATE ASYMMETRY — NO CROSS-BALANCING, AND NO REDRAFT LOOP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The technical engine (`appointments-engine.ts`) carries CC-Q3's redraft loop, which re-draws
 * until every judge's SJ/TJ split is inside tolerance. **This engine deliberately has no
 * equivalent**, and the absence is the specification rather than an omission. §B5: *"not all
 * umpires do video umpiring, so on-field and VU game counts are not balanced against each
 * other. They are tracked as separate counts with no cross-balancing rule."* §B6 says the same
 * thing about the surface: there is no role-total column, because *"a heavy video load is not
 * interchangeable with on-field appointments, so a single total would hide exactly the
 * imbalance you are checking for."*
 *
 * So on-field load is balanced against on-field load, video against video and reserve against
 * reserve — three separate ledgers, never summed and never traded off. {@link UmpireWorkload}
 * carries no `total` field for the same reason, and adding one would be a change to the
 * specification and not to this file.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PURITY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * No I/O, no clock, no randomness. Every ordering decision is derived from the inputs or
 * supplied explicitly through {@link UmpireDraftInput.umpire_order} and
 * {@link UmpireDraftInput.rotation_offset}, so the same inputs always produce the same draft.
 *
 * British English throughout, per the standing copy rule.
 */
import { fixtureVisitOrder, hash32 } from "./appointments-engine.js";
/**
 * Draft order within a match: the two on-field umpires first — they are the scarce resource,
 * capped at one per person per day — then the reserve, then the video umpire.
 *
 * It is also the column order §B4 prints, so a reader of the fixture list and a reader of this
 * file see the same four things in the same sequence.
 */
export const UMPIRE_SLOTS = ["U1", "U2", "RU", "VU"];
/** The two slots §B8 counts as `On-Field`. RU is `Reserve` and VU is `Video`. */
export const ON_FIELD_SLOTS = ["U1", "U2"];
/** True for U1 and U2 — the duties `umpire_onfield_per_day` caps (§B5 rule 3). */
export function isOnFieldSlot(slot) {
    return slot === "U1" || slot === "U2";
}
/** The three values, in the order the profile's editor offers them. */
export const UMPIRE_TYPES = ["leading", "general", "upcoming"];
/**
 * What an umpire is when nobody has said otherwise — `general`, matching migration 042's
 * `DEFAULT 'general'`.
 *
 * The default is the MIDDLE value rather than `leading`, and that is deliberate. A panel
 * defaulted to `leading` would pair with anybody, so the rule would do nothing at all until
 * every umpire had been classified by hand; defaulted to `upcoming` it would refuse almost
 * every pair on an unclassified panel. `general` is the only value whose own default behaviour
 * — General with General is legal — leaves an unclassified panel drafting exactly as it drafted
 * before the column existed.
 */
export const DEFAULT_UMPIRE_TYPE = "general";
export function isUmpireType(value) {
    return UMPIRE_TYPES.includes(value);
}
/** An umpire's type, with the column's default applied to anyone who carries none. */
export function umpireTypeOf(umpire) {
    return umpire.umpire_type ?? DEFAULT_UMPIRE_TYPE;
}
/**
 * **The on-field pairing rule** (§4A, rules c and d), in one place.
 *
 * The written rules are two sentences: *Leading may pair with anyone*, and *General may not
 * pair with Upcoming*. Read literally they leave **Upcoming with Upcoming** unstated, which
 * would put the two least experienced umpires on the panel together on a match with nobody
 * senior on the pitch — the arrangement the rule plainly exists to prevent, reachable only
 * because nobody wrote the third sentence down.
 *
 * **So this function closes that gap deliberately**: `Upcoming` pairs ONLY with `Leading`. It
 * is a decision rather than a reading, it is recorded here rather than inferred at a call site,
 * and it is the one place to change if the Technical Delegate ever rules the other way.
 *
 * The relation is symmetric — a pair is a pair, and which of the two happens to be drafted
 * first is an accident of the walk rather than a fact about the match.
 *
 * Every illegal pair is fixed by the same thing, and that is why one chip string can carry all
 * of them: General + Upcoming, Upcoming + General and Upcoming + Upcoming are each legal only
 * once one of the two is a Leading umpire.
 */
export function umpirePairIsLegal(a, b) {
    if (a === "leading" || b === "leading")
        return true;
    return a === "general" && b === "general";
}
/** The unconfigured tournament's umpire rule set — every rule on, the thresholds at their own. */
export const DEFAULT_UMPIRE_CONFIG = {
    umpire_onfield_per_day: 1,
    own_country_enabled: true,
    avoid_vu_enabled: true,
    umpire_onfield_consecutive_days: 3,
};
export const UMPIRE_BLOCKED_REASONS = [
    "Own Country",
    "Opposite Gender Match",
    "Already In This Match",
    "On-Field Today",
    "Consecutive Days On-Field",
    "Pair Needs A Leading Umpire",
    "No Legal Partner",
    "Avoids Video",
];
/**
 * A `YYYY-MM-DD` as a day number, so consecutive CALENDAR days can be recognised across month
 * and year ends without a calendar library and without a clock.
 *
 * `Date.parse` on an ISO date with an explicit `Z` is UTC by definition, so this is the same
 * number on every host — which matters, because a draft has to be reproducible on the
 * Technical Delegate's laptop and on the server that produced it.
 */
function dayNumber(date) {
    return Math.floor(Date.parse(`${date}T00:00:00Z`) / 86_400_000);
}
/**
 * How long a run of consecutive calendar days becomes if `added` joins `held`.
 *
 * The run is the one THROUGH `added` — days before and after it are walked outwards until the
 * chain breaks — because that is the only run this appointment could lengthen. A run somewhere
 * else in the fortnight is somebody else's problem, or the evaluate pass's.
 *
 * A date already in `held` produces the run it was already part of rather than one longer,
 * which is what a set does and what the arithmetic should say: the same day twice is one day.
 */
function consecutiveOnFieldRun(held, added) {
    const days = new Set(held.map(dayNumber));
    const at = dayNumber(added);
    days.add(at);
    let length = 1;
    for (let day = at - 1; days.has(day); day -= 1)
        length += 1;
    for (let day = at + 1; days.has(day); day += 1)
        length += 1;
    return length;
}
/**
 * The longest run of consecutive calendar days anywhere in a set of on-field dates — what the
 * evaluate pass reports, as opposed to what the draft refuses.
 */
export function longestOnFieldRun(dates) {
    const days = [...new Set(dates.map(dayNumber))].sort((a, b) => a - b);
    let longest = 0;
    let run = 0;
    let previous = null;
    for (const day of days) {
        run = previous !== null && day === previous + 1 ? run + 1 : 1;
        previous = day;
        if (run > longest)
            longest = run;
    }
    return longest;
}
/**
 * **The single definition of "this umpire may not take this slot".**
 *
 * The draft's candidate filter and Run 34's dropdown read the same function, so a name the
 * engine will not propose and a name the picker greys out can never disagree — which is
 * exactly how a TD comes to believe the engine has overlooked somebody.
 *
 * Returns the chip's string, or null when nothing blocks the pick. The order is §B10's own
 * reading order, and the FIRST reason wins: a chip carries one string, so a candidate who is
 * both from a competing country and already in the match is reported as `Own Country`.
 */
export function umpireBlockedReason(umpire, context) {
    const { fixture, slot, config } = context;
    if (config.own_country_enabled &&
        (umpire.country === fixture.home_team || umpire.country === fixture.away_team)) {
        return "Own Country";
    }
    // Cross Gender (§4A, rule a) — EVERY slot, not just the on-field pair. Second in the reading
    // order because, like own country, it is a fact about the person and the match and needs no
    // knowledge of who else is in it.
    if (isOppositeGenderBlocked(umpire, fixture))
        return "Opposite Gender Match";
    // §B5 rule 1 — two roles in one match. Physically impossible, no override.
    if (context.membersOfMatch.includes(umpire.tms_person_id))
        return "Already In This Match";
    // §B5 rule 3 — the on-field cap, counted across both competitions. RU and VU are never
    // capped and never counted, so this can only ever block U1 and U2.
    if (isOnFieldSlot(slot) && context.onFieldToday >= config.umpire_onfield_per_day) {
        return "On-Field Today";
    }
    // The consecutive-days limit (§4A, rule e). On-field only, for the reason the config field
    // gives, and counted over calendar days rather than match days.
    if (isOnFieldSlot(slot) &&
        consecutiveOnFieldRun(context.onFieldDates ?? [], fixture.date) >
            config.umpire_onfield_consecutive_days) {
        return "Consecutive Days On-Field";
    }
    // The pairing rule (§4A, rules c and d), in its two halves — the seat already beside this one,
    // and the seat that would have to be filled after it.
    const partner = context.onFieldPartner ?? null;
    const partnerIsSeated = partner !== null && partner.tms_person_id !== umpire.tms_person_id;
    if (isOnFieldSlot(slot) && partnerIsSeated) {
        // The OTHER on-field seat is taken, so there is a real pair to judge.
        if (!umpirePairIsLegal(umpireTypeOf(umpire), umpireTypeOf(partner))) {
            return "Pair Needs A Leading Umpire";
        }
    }
    else if (isOnFieldSlot(slot) && !hasAvailablePartner(umpire, context)) {
        return "No Legal Partner";
    }
    // Avoid VU (§4). Last of them all, because it is the narrowest: it can only ever block one
    // slot, and only for an umpire somebody has deliberately flagged.
    if (config.avoid_vu_enabled && slot === "VU" && umpire.avoid_vu === true) {
        return "Avoids Video";
    }
    return null;
}
/**
 * **The Cross Gender test, in one place** — read by the candidate filter and by the evaluate
 * pass, so a draft the engine refuses to make and an appointment it flags in red can never
 * disagree about what "opposite gender" means.
 *
 * Two conditions have to hold before it bites, and BOTH are load-bearing:
 *
 *  1. The umpire has been restricted — `cross_gender === false`. Absent and true are the same
 *     answer, which is the behaviour the system had before the column existed.
 *  2. The umpire's own gender is KNOWN. An unstated gender is the ordinary condition of a TMS
 *     import (migration 035), and it is not a claim about anybody: an umpire whose gender
 *     nobody recorded is eligible everywhere, because there is nothing here to compare the
 *     competition against. The restriction is a rule about a mismatch, and a mismatch needs two
 *     values.
 *
 * The competition is `m`/`w` and the person is `m`/`f` — two different vocabularies for the
 * same distinction, mapped here rather than at four call sites that could each map it once and
 * differently.
 */
/**
 * **Could anybody else take the other on-field seat, if this umpire took this one?**
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SEAT ORDER MUST NOT MANUFACTURE AN EMPTY SEAT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The draft fills U1 before U2, and until RUN 41 that order was immaterial: every rule in §B5
 * judged one person against a match, so whoever was legal for U1 was legal for U1 whatever came
 * afterwards. The pairing rule is the first that judges a person against ANOTHER PERSON, and it
 * turned the walk's order into a decision.
 *
 * The defect it produced was found by eye rather than by any rule check, on a panel with one
 * Upcoming umpire and no Leading one at all. U1 came first, the Upcoming umpire scored lowest on
 * the on-field ledger, and he was seated — legally, because there was nobody beside him yet.
 * Then U2 was filled from a panel of General umpires, none of whom may pair with him, and the
 * seat stayed EMPTY. No rule had been broken and the draft was still wrong: the engine had
 * spent a pitch to seat one umpire it could have seated at RU, and handed the Technical
 * Delegate a match with one on-field umpire on it.
 *
 * So an on-field seat is refused unless the pair it starts could actually be completed. The
 * test runs only when the other on-field seat is still EMPTY — once somebody is sitting in it
 * there is a real pair, and {@link umpirePairIsLegal} judges that instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE TEST IS SHALLOW, AND WHY THAT IS THE SAFE DIRECTION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It asks four things of a prospective partner and deliberately no more: they are an umpire,
 * they are not this candidate, they are not already in this match, they are available on the
 * day, they are not shut out of this competition by Cross Gender — and the pair would be legal.
 *
 * It does NOT re-run the on-field-per-day cap, the consecutive-days limit, the own-country rule
 * or the simultaneous-kick-off check. Two reasons, and both matter:
 *
 *  1. **Those depend on the draft's RUNNING COUNTERS**, which are the pass's private state.
 *     Reproducing them here would put a second copy of the whole candidate filter inside the
 *     candidate filter — the exact duplication {@link umpireBlockedReason} exists to prevent —
 *     and §B10's dropdown, which has no counters at all, could not evaluate it.
 *  2. **A shallow test errs towards OFFERING the seat**, and that is the direction to err in.
 *     Missing a rule here means the engine still seats somebody and may still leave the partner
 *     seat empty — which is the behaviour it had yesterday, and no worse. Adding a rule here
 *     means refusing a seat that was in fact fillable, which invents an empty seat rather than
 *     preventing one. This function may only ever prove a partner IMPOSSIBLE, never merely
 *     unlikely.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND IT IS A NO-OP ON AN UNCLASSIFIED PANEL
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * With every umpire at the default `general`, every pair is legal, so the test reduces to "is
 * there another umpire available for this match at all" — which on any real panel is yes, and
 * the draft is untouched. That is what keeps the offset-0 pin byte-identical, and the pin is
 * how it is checked rather than by argument.
 */
function hasAvailablePartner(umpire, context) {
    const panel = context.panel;
    if (panel === undefined)
        return true;
    const type = umpireTypeOf(umpire);
    return panel.some((other) => {
        if (other.tms_person_id === umpire.tms_person_id)
            return false;
        if (!isDraftableUmpire(other))
            return false;
        if (context.membersOfMatch.includes(other.tms_person_id))
            return false;
        if (isUnavailable(other, context.fixture.date))
            return false;
        if (isOppositeGenderBlocked(other, context.fixture))
            return false;
        return umpirePairIsLegal(type, umpireTypeOf(other));
    });
}
function isOppositeGenderBlocked(umpire, fixture) {
    if (umpire.cross_gender !== false)
        return false;
    const gender = umpire.gender ?? null;
    if (gender === null)
        return false;
    return fixture.competition === "m" ? gender === "f" : gender === "m";
}
/* ────────────────────────────────────────────────────────────────────────────
 * 2. Deterministic ordering helpers
 * ──────────────────────────────────────────────────────────────────────────── */
/** Plain lexicographic comparison — no locale, so no host-dependent ordering. */
function compareStrings(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
/** Schedule order, identical to the technical engine's, so both read a day the same way. */
export function compareUmpireFixtures(a, b) {
    return (compareStrings(a.date, b.date) ||
        compareStrings(a.time, b.time) ||
        compareStrings(a.competition, b.competition) ||
        compareStrings(a.match_no, b.match_no) ||
        a.tms_match_db_id - b.tms_match_db_id);
}
/**
 * Rank of each umpire for the final tie-break, honouring `umpire_order` and the offset.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT RUN 40 MEASURED BEFORE CHANGING ANYTHING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * RUN 38b found the technical engine's plain modular rotation of this index to be very nearly
 * INERT: `Re-Draft` produced byte-identical drafts and the Technical Delegate found it by
 * pressing the button three times. RUN 40 checked this engine against the same yardstick
 * before touching it, over the corpus's own sixty pool matches and twenty umpires — 240
 * advisory slots — and found something different and less obvious.
 *
 * The plain rotation here was NOT inert. Consecutive offsets moved between 208 and 236 of the
 * 240 assignments, because an umpire draft has four slots a match against three, a much
 * flatter set of scoring components (§B5 forbids the cross-balancing that separates the
 * technical engine's candidates) and therefore far more genuine ties for this index to break.
 *
 * What it DID have was a cycle. `(position - offset) mod size` repeats every `size` offsets,
 * so on the twenty-strong panel the corpus carries, **offset 20 was byte-identical to offset
 * 0** — measured, not inferred — and so was offset 40. A Technical Delegate pressing
 * `Re-Draft` twenty times would have been handed back the draft he started with, with a
 * notice calling it variant 20. That is the RUN 38 defect wearing a different number, and it
 * is the reason this function now does what its technical sibling does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SO: A PERMUTATION, NOT A ROTATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The offset now selects a whole permutation of the ranking — umpires are ranked by
 * `hash32(id|offset)`, with the id itself as the final tie-break so the map is total and
 * stable — and the offset is NOT reduced modulo the panel size. A hash has no cycle to wrap
 * around, so variant 20 is its own arrangement rather than variant 0's.
 *
 * `hash32` is the technical engine's, imported rather than copied: two engines whose variants
 * were built by two copies of the same idea would be free to drift apart, and "variant 2"
 * must mean the same kind of thing on both pages.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * OFFSET 0 IS THE IDENTITY, AND THAT IS A HARD REQUIREMENT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Offset 0 returns the plain positional ranking — `umpire_order` first, then ascending id —
 * exactly as this function always has. Every advisory draft this system has ever stored was
 * offset 0, `Draft The Pool Stage` and `Draft All` still send no offset at all, and every
 * other test in this engine's suite asserts against it. It is pinned by a snapshot-style test
 * rather than left to inspection.
 */
function buildOrderIndex(umpires, explicitOrder, rotationOffset) {
    const ids = umpires.map((umpire) => umpire.tms_person_id);
    const ordered = [];
    if (explicitOrder) {
        for (const id of explicitOrder) {
            if (ids.includes(id) && !ordered.includes(id))
                ordered.push(id);
        }
    }
    for (const id of [...ids].sort(compareStrings)) {
        if (!ordered.includes(id))
            ordered.push(id);
    }
    const ranked = rotationOffset === 0
        ? ordered
        : [...ordered].sort((a, b) => hash32(`${a}|${String(rotationOffset)}`) - hash32(`${b}|${String(rotationOffset)}`) ||
            compareStrings(a, b));
    const index = new Map();
    ranked.forEach((id, position) => {
        index.set(id, position);
    });
    return index;
}
function compareScores(a, b) {
    const length = Math.max(a.length, b.length);
    for (let i = 0; i < length; i += 1) {
        const left = a[i] ?? 0;
        const right = b[i] ?? 0;
        if (left !== right)
            return left - right;
    }
    return 0;
}
function slotKey(matchId, slot) {
    return `${String(matchId)}|${slot}`;
}
function bump(map, key, by = 1) {
    map.set(key, (map.get(key) ?? 0) + by);
}
/** Only an Umpire is drafted onto an umpire slot; nobody else is on offer either. */
function isDraftableUmpire(umpire) {
    return umpire.role === "Umpire";
}
function isUnavailable(umpire, date) {
    return (umpire.unavailable_dates ?? []).includes(date);
}
/** Two matches clash when they start at the same time on the same day, in either competition. */
function clashes(a, b) {
    return a.date === b.date && a.time === b.time;
}
function emptyCounters() {
    return {
        onField: new Map(),
        video: new Map(),
        reserve: new Map(),
        onFieldPerDay: new Map(),
        onFieldDates: new Map(),
        perDay: new Map(),
        teamExposure: new Map(),
        onFieldTogether: new Map(),
    };
}
function creditAssignment(counters, officialId, slot, fixture, 
/** Who is already in this match, and in which slot — for the on-field pairing count. */
members) {
    if (isOnFieldSlot(slot)) {
        counters.onField.set(officialId, (counters.onField.get(officialId) ?? 0) + 1);
        bump(counters.onFieldPerDay, `${officialId}|${fixture.date}`);
        const dates = counters.onFieldDates.get(officialId) ?? new Set();
        dates.add(fixture.date);
        counters.onFieldDates.set(officialId, dates);
    }
    else if (slot === "VU") {
        counters.video.set(officialId, (counters.video.get(officialId) ?? 0) + 1);
    }
    else {
        counters.reserve.set(officialId, (counters.reserve.get(officialId) ?? 0) + 1);
    }
    bump(counters.perDay, `${officialId}|${fixture.date}`);
    // §B7's foot: "An umpire covers BOTH teams of any match they are appointed to, in any of the
    // four roles, so each appointment adds two counts."
    for (const team of [fixture.home_team, fixture.away_team]) {
        if (team !== null)
            bump(counters.teamExposure, `${officialId}|${team}`);
    }
    // §B5 rule 4, partner rotation — the U1/U2 PAIRING, and only that. A reserve and a video
    // umpire are not partners in any sense the rotation rule means, and counting them as such
    // would let a busy reserve steer the on-field pairings.
    if (!isOnFieldSlot(slot))
        return;
    for (const other of members) {
        if (other.id === officialId || !isOnFieldSlot(other.slot))
            continue;
        bump(counters.onFieldTogether, `${officialId}|${other.id}`);
        bump(counters.onFieldTogether, `${other.id}|${officialId}`);
    }
}
/* ────────────────────────────────────────────────────────────────────────────
 * 4. AVOID VU — the new field, in one place
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate, 2026-08-28: *"add a field for umpires (Avoid VU) and if that field
 * is checked for an umpire, this umpire should not be considered for any VU duties (i.e. do not
 * assign/draft him as VU for any matches) when I use the AI draft function for Umpire
 * Appointments."*
 *
 * Read that sentence closely, because its limits are as load-bearing as its instruction:
 *
 *  1. **It is a hard candidate filter on the VU slot, not a score.** A flagged umpire is not
 *     ranked last for video duty; they are not a candidate at all. If nobody else can take the
 *     slot, the slot stays EMPTY — reported as `unfilled_slot` — rather than being filled by
 *     the one person the TD said not to put there. A "soft" reading would produce exactly the
 *     appointment the flag exists to prevent, on precisely the day the panel is thinnest.
 *
 *  2. **It affects the VU slot and nothing else.** U1, U2 and RU eligibility are untouched. An
 *     umpire who avoids video is an umpire, and the flag says nothing about their on-field or
 *     reserve availability.
 *
 *  3. **It is a DRAFT rule, not a violation.** §B5's violation list has two entries and this is
 *     not one of them. If a Manager appoints a flagged umpire as VU in TMS, that appointment
 *     appears here exactly as TMS holds it: no red cell, no chip, no count against the
 *     compliance figure. The Managers own umpire appointments (§B1) and the flag is the TD's
 *     drafting preference, not a rule he may enforce on them.
 *
 * The filter itself lives in {@link umpireBlockedReason} — one definition, read by the draft
 * and by the dropdown — and the surfaced chip is `Avoids Video`.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * 4A. THE RUN 41 ATTRIBUTES — CROSS GENDER, UMPIRE TYPE, CONSECUTIVE DAYS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Three more standing facts about one umpire, recorded on the profile beside Avoid VU and read
 * by this engine and nothing else. They are set out together because the interesting thing
 * about them is not what each one does — that is short — but that they do NOT all do the same
 * kind of thing, and the differences are decisions rather than accidents.
 *
 * ── (a) CROSS GENDER — a candidate filter on every slot, AND a violation ────
 *
 * `cross_gender = false` means: never draft this umpire onto a match of the opposite gender to
 * their own, in ANY of the four slots. Unlike Avoid VU it is not narrowed to one duty, because
 * the fact it records is about the match rather than about the job — an umpire who does not
 * work the women's competition does not work it from the video booth either.
 *
 * **Unknown gender is NOT restricted.** See {@link isOppositeGenderBlocked}: the rule compares
 * two values, and a TMS import that carries no gender for somebody has given the engine only
 * one of them. Defaulting the missing half would turn an absence into a claim about a person,
 * which is the mistake `statedGender` exists elsewhere in this system to prevent.
 *
 * ── (c)/(d) UMPIRE TYPE — a pairing rule on the ON-FIELD PAIR ONLY ──────────
 *
 * Leading may pair with anyone; General may not pair with Upcoming; and — the sentence the
 * written rules leave out, closed deliberately here — Upcoming may pair ONLY with Leading, so
 * Upcoming with Upcoming is blocked too. {@link umpirePairIsLegal} is the whole of it, and its
 * doc-comment carries the reasoning for the third case.
 *
 * **The pair is U1 + U2 and nothing else.** RU and VU are not part of it, exactly as they are
 * not part of §B5 rule 4's partner rotation: a reserve umpire and a video umpire are not
 * standing on the pitch with anybody, and letting them count would let a busy reserve dictate
 * who may umpire.
 *
 * **It bites on whichever of the two is chosen second**, and equally when the TD has locked one
 * of them by hand: the draft seats U1 before U2, so the ordinary case is U2 being judged
 * against a known U1 — but a hand-edited U2 is placed first and is then the partner U1 is
 * judged against. That is why the partner travels in {@link UmpireCandidateContext} rather than
 * being derived from a slot name.
 *
 * ── (e) CONSECUTIVE DAYS — a candidate filter, but only an ADVISORY finding ─
 *
 * No more than `umpire_onfield_consecutive_days` calendar days of on-field duty in a row,
 * default 3. The draft refuses the seat that would make it four; the evaluate pass REPORTS a
 * longer run rather than colouring it red.
 *
 * ── WHY THE SEVERITIES DIFFER, WHICH IS THE POINT OF THIS SECTION ──────────
 *
 * §4 reading 3 says Avoid VU produces no warning at all, because it is the TD's drafting
 * preference and the Umpires Managers own the appointments (§B1). That reasoning is kept, and
 * it is also the reason the three new attributes do not all follow it:
 *
 *   * `cross_gender` is a VIOLATION, like `own_country`. It is not a preference about who is
 *     nicer to draft — it is a statement that this person does not officiate that competition,
 *     and a Manager who has appointed them to one has appointed somebody who will not be there.
 *     The TD still cannot correct it here (§B4); he can raise it, and for that he has to see it.
 *   * `pairing` is a VIOLATION for the same reason: an Upcoming umpire alone on the pitch with
 *     another Upcoming umpire is the arrangement the rule exists to prevent, whoever made it.
 *   * `consecutive_days` is ADVISORY, and deliberately not red. A fourth day running is a load
 *     question rather than a breach — the panel may genuinely be too thin to avoid it, and a
 *     red chip the TD cannot clear on work he cannot edit would be an accusation rather than
 *     information. It sits beside `unfilled_slot`, which is reported for the same reason.
 *   * `avoid_vu` keeps its silence. NOTHING here changes §4 reading 3.
 *
 * ── WHAT ALL FOUR SHARE: DEFAULTS THAT CHANGE NOTHING ──────────────────────
 *
 * Cross Gender defaults to true, Umpire Type to `general` and the day limit to 3, and a panel
 * on which nobody has been classified drafts EXACTLY as it drafted before these columns
 * existed. That is asserted rather than asserted-to-be-obvious: the offset-0 pin in
 * `umpire-engine.test.ts` is byte-identical to the one RUN 40 left behind.
 */
/* ────────────────────────────────────────────────────────────────────────────
 * 5. The single draft pass
 * ──────────────────────────────────────────────────────────────────────────── */
function draftPass(fixtures, umpires, config, locked, orderIndex, 
/** `null` = every fixture is this pass's to fill. See {@link UmpireDraftInput.draft_scope}. */
draftable, 
/**
 * Where in the schedule this pass starts its walk (RUN 40). See `fixtureVisitOrder` in the
 * technical engine, whose reasoning applies here unchanged: every candidate filter in this
 * pass is checked against inputs or running counters and never against a fixture's position,
 * so moving the starting point cannot weaken §B5's rules. It changes which of two equally
 * legal arrangements comes out.
 */
rotationOffset = 0) {
    const lockedByKey = new Map(locked.map((entry) => [slotKey(entry.tms_match_db_id, entry.slot), entry.official_id]));
    const slots = [];
    const counters = emptyCounters();
    /** Umpire → the indices of the fixtures they already hold a slot in. */
    const bookings = new Map();
    /**
     * The panel by id, so the pairing rule can resolve the seated partner to a whole umpire. A
     * locked cell may name somebody who is not on the supplied panel at all — a role correction,
     * or a hand edit made before a withdrawal — and that lookup yields `undefined`, which reads
     * as "no partner known" and blocks nothing. The draft never invents a type for a person it
     * cannot see.
     */
    const umpireById = new Map(umpires.map((umpire) => [umpire.tms_person_id, umpire]));
    const membersByFixture = fixtures.map(() => []);
    const book = (officialId, slot, fixtureIndex) => {
        const fixture = fixtures[fixtureIndex];
        if (fixture === undefined)
            return;
        const members = membersByFixture[fixtureIndex] ?? [];
        creditAssignment(counters, officialId, slot, fixture, members);
        members.push({ id: officialId, slot });
        membersByFixture[fixtureIndex] = members;
        const held = bookings.get(officialId) ?? [];
        held.push(fixtureIndex);
        bookings.set(officialId, held);
    };
    // Hand-edited cells are placed first, across EVERY fixture — including the carried context —
    // so the slots this pass chooses are chosen against the whole picture rather than against
    // today alone. This is what lets a day be drafted without being drafted blind.
    fixtures.forEach((fixture, fixtureIndex) => {
        for (const slot of UMPIRE_SLOTS) {
            const lockedOfficial = lockedByKey.get(slotKey(fixture.tms_match_db_id, slot));
            if (lockedOfficial === undefined)
                continue;
            slots.push({ fixtureIndex, slot, official: lockedOfficial, locked: true });
            book(lockedOfficial, slot, fixtureIndex);
        }
    });
    // The drafting walk, started wherever the offset says. The hand-edit loop above deliberately
    // stays in schedule order: it places facts rather than making choices, and its counters come
    // out the same whichever order they are credited in.
    //
    // `slots` is therefore pushed in visit order rather than schedule order, which nothing
    // downstream minds: `toResult` sorts by fixture index before it builds the appointments and
    // every warning goes through `sortWarnings`. A draft's OUTPUT order is schedule order at
    // every offset; only the order it was decided in moves.
    for (const fixtureIndex of fixtureVisitOrder(fixtures.length, rotationOffset)) {
        const fixture = fixtures[fixtureIndex];
        if (fixture === undefined)
            continue;
        const inScope = draftable === null || draftable.has(fixture.tms_match_db_id);
        for (const slot of UMPIRE_SLOTS) {
            if (lockedByKey.has(slotKey(fixture.tms_match_db_id, slot)))
                continue;
            // Carried context: no `WorkingSlot` is pushed at all, so its empty slots cannot be
            // drafted, cannot be reported as unfilled and cannot reach the caller as a row to store.
            if (!inScope)
                continue;
            const members = membersByFixture[fixtureIndex] ?? [];
            const memberIds = members.map((member) => member.id);
            // The on-field partner, where the other half of the pair is already seated — by a hand
            // edit placed before the walk began, or by this same walk a moment ago. Null for RU and
            // VU, which are not part of the pair at all (§4A).
            const seatedPartner = isOnFieldSlot(slot)
                ? (members.find((member) => isOnFieldSlot(member.slot)) ?? null)
                : null;
            const onFieldPartner = seatedPartner === null ? null : (umpireById.get(seatedPartner.id) ?? null);
            const candidates = umpires.filter((umpire) => {
                const id = umpire.tms_person_id;
                if (!isDraftableUmpire(umpire))
                    return false;
                if (isUnavailable(umpire, fixture.date))
                    return false;
                // Physically impossible — the same person at two simultaneous matches. Not one of
                // §B5's five rules, and it does not need to be: it is arithmetic about a clock.
                const held = bookings.get(id) ?? [];
                if (held.some((other) => {
                    const otherFixture = fixtures[other];
                    return otherFixture !== undefined && clashes(otherFixture, fixture);
                })) {
                    return false;
                }
                // §B5 rules 1, 2 and 3, the Avoid VU filter and the three RUN 41 attributes — every one
                // of them through the ONE definition the dropdown also reads, so a name the engine
                // skips is a name the picker greys out.
                return (umpireBlockedReason(umpire, {
                    fixture,
                    slot,
                    membersOfMatch: memberIds,
                    onFieldToday: counters.onFieldPerDay.get(`${id}|${fixture.date}`) ?? 0,
                    onFieldPartner,
                    onFieldDates: [...(counters.onFieldDates.get(id) ?? [])],
                    // The whole panel, for the partner-feasibility test alone. The draft can see it, so
                    // it is the caller that must supply it — see `hasAvailablePartner`.
                    panel: umpires,
                    config,
                }) === null);
            });
            if (candidates.length === 0) {
                // Including the case the Avoid VU rule creates on a thin panel: the VU slot stays
                // empty rather than being filled by somebody the TD flagged. See §4, reading 1.
                slots.push({ fixtureIndex, slot, official: null, locked: false });
                continue;
            }
            /**
             * Lexicographic, like the technical engine — but the FIRST component differs by slot,
             * and that is the whole of §B5's asymmetry expressed in one line: an on-field slot
             * balances on-field load, a video slot balances video load, a reserve slot balances
             * reserve load. No component of any of these three vectors reads another ledger, so
             * nothing here can trade a video duty against an on-field one.
             */
            const score = (umpire) => {
                const id = umpire.tms_person_id;
                const ownLedger = isOnFieldSlot(slot)
                    ? (counters.onField.get(id) ?? 0)
                    : slot === "VU"
                        ? (counters.video.get(id) ?? 0)
                        : (counters.reserve.get(id) ?? 0);
                const exposure = [fixture.home_team, fixture.away_team]
                    .filter((team) => team !== null)
                    .map((team) => counters.teamExposure.get(`${id}|${team}`) ?? 0);
                // Only meaningful when filling U1 or U2; for RU and VU every term is zero, because
                // `onFieldTogether` is only ever credited between two on-field umpires.
                const colleagueHeat = members.reduce((sum, other) => sum + (isOnFieldSlot(slot) ? (counters.onFieldTogether.get(`${id}|${other.id}`) ?? 0) : 0), 0);
                return [
                    ownLedger,
                    exposure.length === 0 ? 0 : Math.max(...exposure),
                    colleagueHeat,
                    counters.perDay.get(`${id}|${fixture.date}`) ?? 0,
                    orderIndex.get(id) ?? 0,
                ];
            };
            let best = candidates[0];
            let bestScore = score(best);
            for (const candidate of candidates.slice(1)) {
                const candidateScore = score(candidate);
                if (compareScores(candidateScore, bestScore) < 0) {
                    best = candidate;
                    bestScore = candidateScore;
                }
            }
            slots.push({ fixtureIndex, slot, official: best.tms_person_id, locked: false });
            book(best.tms_person_id, slot, fixtureIndex);
        }
    }
    return slots;
}
/* ────────────────────────────────────────────────────────────────────────────
 * 6. Measuring — §B5's two violations, and nothing else red
 * ──────────────────────────────────────────────────────────────────────────── */
function describe(umpire, id) {
    return umpire === undefined ? id : `${umpire.surname} (${umpire.country})`;
}
/** The competition as a Technical Delegate says it, for a sentence rather than a column. */
function competitionName(competition) {
    return competition === "m" ? "men's" : "women's";
}
/** An umpire type as the profile prints it — `Leading`, `General`, `Upcoming`. */
export function umpireTypeLabel(type) {
    return `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
}
function collectWarnings(slots, fixtures, umpires, config, unfilledCause) {
    const byId = new Map(umpires.map((umpire) => [umpire.tms_person_id, umpire]));
    const warnings = [];
    const membersByFixture = new Map();
    for (const entry of slots) {
        if (entry.official === null)
            continue;
        const members = membersByFixture.get(entry.fixtureIndex) ?? [];
        members.push(entry.official);
        membersByFixture.set(entry.fixtureIndex, members);
    }
    for (const entry of slots) {
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        if (entry.official === null) {
            // The sentence depends on WHY the slot is empty — see {@link UnfilledSlotCause} in the
            // technical engine. Only a draft pass that exhausted its candidates may claim "no
            // eligible umpire could be drafted"; a stored slot no draft has touched is merely not
            // yet appointed, and the drafting words there misled a TD into thinking drafting was
            // broken (Day 11, 28 Sep 2026 — the same finding as the technical engine's).
            warnings.push({
                code: "unfilled_slot",
                severity: "advisory",
                reason: null,
                message: unfilledCause === "no_candidate"
                    ? `${fixture.match_no} — no eligible umpire could be drafted as ${entry.slot}. ` +
                        `The slot is left empty rather than filled by somebody the rules exclude.`
                    : `${fixture.match_no} — ${entry.slot} not yet appointed. The set simply does ` +
                        `not hold an umpire here yet; nothing has been excluded.`,
                official_id: null,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: {
                    match_no: fixture.match_no,
                    date: fixture.date,
                    time: fixture.time,
                    cause: unfilledCause,
                },
            });
            continue;
        }
        const umpire = byId.get(entry.official);
        // §B5 rule 2 — own country. Red, on every slot the person holds in that match.
        if (config.own_country_enabled &&
            umpire !== undefined &&
            (umpire.country === fixture.home_team || umpire.country === fixture.away_team)) {
            warnings.push({
                code: "own_country",
                severity: "violation",
                reason: "Own Country",
                message: `${describe(umpire, entry.official)} is appointed as ${entry.slot} to ` +
                    `${fixture.match_no}, a match involving their own country. Raise it with the ` +
                    `Umpires Managers — it cannot be corrected here.`,
                official_id: entry.official,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: { match_no: fixture.match_no, country: umpire.country },
            });
        }
        // Cross Gender (§4A, rule a). Red, on every slot the person holds in that match, exactly
        // as own country is — the reason is the same shape: this appointment should not exist, and
        // the TD can only raise it with the Umpires Managers.
        if (umpire !== undefined && isOppositeGenderBlocked(umpire, fixture)) {
            warnings.push({
                code: "cross_gender",
                severity: "violation",
                reason: "Opposite Gender Match",
                message: `${describe(umpire, entry.official)} is appointed as ${entry.slot} to ` +
                    `${fixture.match_no}, a ${competitionName(fixture.competition)} match, and does not ` +
                    `umpire across genders. Raise it with the Umpires Managers — it cannot be corrected ` +
                    `here.`,
                official_id: entry.official,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: {
                    match_no: fixture.match_no,
                    competition: fixture.competition,
                    gender: umpire.gender ?? "",
                },
            });
        }
        // The on-field pairing rule (§4A, rules c and d). Reported on BOTH halves of the pair, for
        // the reason `two_roles_one_match` is reported on every slot: the finding is about this
        // cell as much as about the other one, and a chip on only one of the two would leave the
        // TD looking at a red U2 beside a clean U1 and wondering which of them was wrong.
        if (isOnFieldSlot(entry.slot) && umpire !== undefined) {
            const otherSlot = entry.slot === "U1" ? "U2" : "U1";
            const otherEntry = slots.find((candidate) => candidate.fixtureIndex === entry.fixtureIndex &&
                candidate.slot === otherSlot &&
                candidate.official !== null);
            const partner = otherEntry === undefined ? undefined : byId.get(otherEntry.official);
            if (partner !== undefined &&
                partner.tms_person_id !== umpire.tms_person_id &&
                !umpirePairIsLegal(umpireTypeOf(umpire), umpireTypeOf(partner))) {
                warnings.push({
                    code: "pairing",
                    severity: "violation",
                    reason: "Pair Needs A Leading Umpire",
                    message: `${fixture.match_no} pairs ${describe(umpire, entry.official)} ` +
                        `(${umpireTypeLabel(umpireTypeOf(umpire))}) with ` +
                        `${describe(partner, partner.tms_person_id)} ` +
                        `(${umpireTypeLabel(umpireTypeOf(partner))}) on the pitch. One of the two on-field ` +
                        `umpires must be a Leading umpire. Raise it with the Umpires Managers — it cannot ` +
                        `be corrected here.`,
                    official_id: entry.official,
                    tms_match_db_id: fixture.tms_match_db_id,
                    slot: entry.slot,
                    detail: {
                        match_no: fixture.match_no,
                        umpire_type: umpireTypeOf(umpire),
                        partner_type: umpireTypeOf(partner),
                    },
                });
            }
        }
        // §B5 rule 1 — two roles in one match. Reported on EVERY slot the duplicated person
        // holds, which is what §B5 says: "on every slot the duplicated person holds".
        const members = membersByFixture.get(entry.fixtureIndex) ?? [];
        if (members.filter((id) => id === entry.official).length > 1) {
            warnings.push({
                code: "two_roles_one_match",
                severity: "violation",
                reason: "Two Roles In One Match",
                message: `${describe(umpire, entry.official)} holds more than one role in ` +
                    `${fixture.match_no}. One person cannot umpire and hold a second duty in the same ` +
                    `match.`,
                official_id: entry.official,
                tms_match_db_id: fixture.tms_match_db_id,
                slot: entry.slot,
                detail: { match_no: fixture.match_no },
            });
        }
    }
    /*
     * The consecutive-days finding (§4A, rule e) — ONE per umpire, not one per appointment.
     *
     * It is the only finding in this function that is not about a cell. A run of four days is a
     * property of a fortnight and of a person, so it carries no match id and no slot: naming the
     * fourth day's match would put a chip on the one appointment that is no more at fault than
     * the three before it, and §B4's table has no cell to hang it on.
     *
     * Advisory, and never red — see §4A on why this one differs from the two above it.
     *
     * The panel is walked in its own (sorted) order rather than the appointment order, so the
     * findings come out the same however the stored rows happened to arrive.
     */
    const limit = config.umpire_onfield_consecutive_days;
    const onFieldDates = new Map();
    for (const entry of slots) {
        if (entry.official === null || !isOnFieldSlot(entry.slot))
            continue;
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            continue;
        const held = onFieldDates.get(entry.official) ?? [];
        held.push(fixture.date);
        onFieldDates.set(entry.official, held);
    }
    for (const umpire of umpires) {
        const held = onFieldDates.get(umpire.tms_person_id);
        if (held === undefined)
            continue;
        const run = longestOnFieldRun(held);
        if (run <= limit)
            continue;
        warnings.push({
            code: "consecutive_days",
            severity: "advisory",
            reason: null,
            message: `${describe(umpire, umpire.tms_person_id)} holds on-field duty on ${String(run)} ` +
                `consecutive days. The limit for this tournament is ${String(limit)}.`,
            official_id: umpire.tms_person_id,
            tms_match_db_id: null,
            slot: null,
            detail: { days: run, limit },
        });
    }
    return warnings;
}
/*
 * DELIBERATELY ABSENT — an `on_field_today` warning.
 *
 * §B5 rule 3 is explicit: *"NOT a red violation. Enforced only as a selection block in the
 * AI-draft dropdown (flag `On-Field Today`) and stated in the panel foot."* So the cap filters
 * candidates in {@link umpireBlockedReason} and it is stated in §B4's foot, and a set of stored
 * rows that breaches it produces no finding at all. A warning here would put a red chip on the
 * Umpires Managers' work for something the specification says is not a violation — and the TD
 * would have no way to clear it, because he cannot edit those cells.
 *
 * DELIBERATELY ABSENT — an `avoids_video` warning. See §4, reading 3, for the same reason
 * stated at more length: the flag is a drafting preference, not a rule the Managers broke.
 *
 * DELIBERATELY ABSENT — a `vu` warning for the RUN 41 field of that name. The profile now
 * presents that same column as **VU: Yes / No** rather than as a tick-box called Avoid VU, and
 * the change is entirely one of wording: `VU = No` IS `avoid_vu = true`, one column, one
 * filter, one seam. Re-labelling a field does not turn a drafting preference into a compliance
 * rule, so it draws no chip, adds nothing to §B3's compliance figure and appears in no warning
 * list — exactly as it did under its old name.
 *
 * PRESENT, AND EACH FOR A STATED REASON — `cross_gender` and `pairing` as violations,
 * `consecutive_days` as an advisory. §4A sets out why those three do not follow Avoid VU's
 * silence and why the third of them is not red.
 *
 * DELIBERATELY ABSENT — team-exposure and partner-rotation warnings. §B5 rules 4 and 5 are
 * heat matrices with "no hard warning", derived live by the page from the appointment rows
 * (Part C invariant 1). Neither is stored, and neither is reported here.
 */
/**
 * Reading order for the warning list — the three violations first, then the two advisories.
 *
 * Within each group the order is the order a Technical Delegate reads a row of §B4's table in:
 * who should not be here at all (`own_country`, `cross_gender`), then what is wrong with the
 * pair, then the arithmetic about one person holding two things.
 */
const WARNING_ORDER = [
    "own_country",
    "cross_gender",
    "pairing",
    "two_roles_one_match",
    "consecutive_days",
    "unfilled_slot",
];
function sortWarnings(warnings) {
    return [...warnings].sort((a, b) => WARNING_ORDER.indexOf(a.code) - WARNING_ORDER.indexOf(b.code) ||
        (a.tms_match_db_id ?? 0) - (b.tms_match_db_id ?? 0) ||
        UMPIRE_SLOTS.indexOf(a.slot ?? "U1") - UMPIRE_SLOTS.indexOf(b.slot ?? "U1") ||
        compareStrings(a.official_id ?? "", b.official_id ?? ""));
}
/* ────────────────────────────────────────────────────────────────────────────
 * 7. Derived figures — never stored (Part C invariant 1)
 * ──────────────────────────────────────────────────────────────────────────── */
/** §B8's three figures per umpire, derived from a set of appointments. Never summed. */
export function summariseUmpireWorkload(appointments) {
    const counts = new Map();
    for (const appointment of appointments) {
        const id = appointment.official_id;
        if (id === null)
            continue;
        const current = counts.get(id) ?? { on_field: 0, video: 0, reserve: 0 };
        if (isOnFieldSlot(appointment.slot))
            current.on_field += 1;
        else if (appointment.slot === "VU")
            current.video += 1;
        else
            current.reserve += 1;
        counts.set(id, current);
    }
    return [...counts.entries()]
        .sort(([a], [b]) => compareStrings(a, b))
        .map(([official_id, value]) => ({
        official_id,
        on_field: value.on_field,
        video: value.video,
        reserve: value.reserve,
    }));
}
function evennessRow(values) {
    const held = values.filter((value) => value > 0);
    return { used: held.length, max: held.length === 0 ? 0 : Math.max(...held) };
}
/**
 * §B9's evenness note, for one set of appointments. The page computes it for the Umpires
 * Managers' set and for the AI set and prints them side by side; this is the arithmetic, once,
 * so the two halves of that comparison cannot be measured differently.
 */
export function summariseUmpireEvenness(workloads) {
    return {
        on_field: evennessRow(workloads.map((workload) => workload.on_field)),
        reserve: evennessRow(workloads.map((workload) => workload.reserve)),
        video: evennessRow(workloads.map((workload) => workload.video)),
    };
}
/* ────────────────────────────────────────────────────────────────────────────
 * 8. Public entry points
 * ──────────────────────────────────────────────────────────────────────────── */
function toResult(slots, fixtures, umpires, config, unfilledCause) {
    const appointments = [...slots]
        .sort((a, b) => a.fixtureIndex - b.fixtureIndex ||
        UMPIRE_SLOTS.indexOf(a.slot) - UMPIRE_SLOTS.indexOf(b.slot))
        .flatMap((entry) => {
        const fixture = fixtures[entry.fixtureIndex];
        if (fixture === undefined)
            return [];
        return [
            {
                tms_match_db_id: fixture.tms_match_db_id,
                match_no: fixture.match_no,
                competition: fixture.competition,
                date: fixture.date,
                time: fixture.time,
                slot: entry.slot,
                official_id: entry.official,
                origin: entry.locked ? "td_manual" : "ai_draft",
            },
        ];
    });
    const workloads = summariseUmpireWorkload(appointments);
    return {
        appointments,
        warnings: sortWarnings(collectWarnings(slots, fixtures, umpires, config, unfilledCause)),
        workloads,
        evenness: summariseUmpireEvenness(workloads),
    };
}
function prepare(input) {
    const config = { ...DEFAULT_UMPIRE_CONFIG, ...(input.config ?? {}) };
    const fixtures = [...input.fixtures].sort(compareUmpireFixtures);
    const umpires = [...input.umpires].sort((a, b) => compareStrings(a.tms_person_id, b.tms_person_id));
    const rotationOffset = input.rotation_offset ?? 0;
    return {
        fixtures,
        umpires,
        config,
        locked: input.locked ?? [],
        orderIndex: buildOrderIndex(umpires, input.umpire_order, rotationOffset),
        draftable: input.draft_scope === undefined ? null : new Set(input.draft_scope),
        rotationOffset,
    };
}
/**
 * **The engine.** One pass, U1 / U2 / RU / VU, over the fixtures of BOTH competitions.
 *
 * There is no second pass and no redraft loop, and §B5's asymmetry is why: the technical
 * engine re-draws to close an SJ/TJ spread because rule 4 defines one, and no umpire rule
 * defines anything the engine could re-draw towards. Swapping a video duty for an on-field one
 * to "balance" a panel would be the cross-balancing §B5 forbids in as many words.
 *
 * Deterministic: identical inputs always produce an identical draft.
 */
export function draftUmpireAppointments(input) {
    const { fixtures, umpires, config, locked, orderIndex, draftable, rotationOffset } = prepare(input);
    const slots = draftPass(fixtures, umpires, config, locked, orderIndex, draftable, rotationOffset);
    return toResult(slots, fixtures, umpires, config, "no_candidate");
}
/**
 * **Measure, do not draft.** Recomputes §B5's two violations and §B8's three workloads over a
 * set of appointments exactly as supplied, choosing nothing and moving nothing.
 *
 * This is what a GET runs, over **both** datasets separately — the Umpires Managers' actual
 * appointments and the advisory draft — because Part C invariant 1 says every matrix is derived
 * live and never stored, and invariant 5 says the two sets must never be merged. Running the
 * same measuring function over each of them, one at a time, is how the compliance chip on the
 * TMS half and the status pills on the AI half can be trusted to mean the same thing.
 *
 * Because nothing is drafted here, an empty slot gets the "not yet appointed" `unfilled_slot`
 * wording rather than the draft's "no eligible umpire could be drafted" — see
 * {@link UnfilledSlotCause} in the technical engine, which owns the distinction.
 *
 * Pure and deterministic, like everything else here.
 */
export function evaluateUmpireAppointments(input) {
    const config = { ...DEFAULT_UMPIRE_CONFIG, ...(input.config ?? {}) };
    const fixtures = [...input.fixtures].sort(compareUmpireFixtures);
    const umpires = [...input.umpires].sort((a, b) => compareStrings(a.tms_person_id, b.tms_person_id));
    const held = new Map(input.appointments.map((entry) => [
        slotKey(entry.tms_match_db_id, entry.slot),
        entry.official_id,
    ]));
    // Every fixture carries all four slots, filled or not — §B4's table prints four columns
    // whatever TMS holds, and an empty one is a fact about the day rather than an absence.
    const slots = [];
    fixtures.forEach((fixture, fixtureIndex) => {
        for (const slot of UMPIRE_SLOTS) {
            const official = held.get(slotKey(fixture.tms_match_db_id, slot)) ?? null;
            slots.push({ fixtureIndex, slot, official, locked: official !== null });
        }
    });
    // A stored-empty slot is "not yet appointed", never "no candidate": no draft ran here, so
    // nothing can honestly claim to have exhausted the candidates.
    return toResult(slots, fixtures, umpires, config, "not_yet_appointed");
}
//# sourceMappingURL=umpire-engine.js.map